import { get, remove, post, patch } from '../http-client/client';
import { ApiResponse } from '../http-client/Response';
import instance from '../http-client/client';

export interface FileSearchStore {
    id: string;
    name: string;
    description?: string;
    documentCount: number;
    organizationId: string;
    createdById: string;
    createdAt: string;
    updatedAt: string;
}

export interface FileSearchDocument {
    id: string;
    storeId: string;
    displayName: string;
    sizeBytes?: string;
    state?: string;
    uploadedById: string;
    googleCreateTime?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateStoreDto {
    name: string;
    description?: string;
}

export interface UpdateStoreDto {
    name?: string;
    description?: string;
}

export interface ListDocumentsResponse {
    documents: FileSearchDocument[];
    nextPageToken?: string;
}

export interface Pager<T> {
    page: T[];
    pageInfo?: {
        nextPageToken?: string;
    };
    hasNextPage: () => boolean;
    nextPage: () => Promise<T[]>;
}

/**
 * Get all file search stores for the user's organization
 */
export const getStores = async (): Promise<FileSearchStore[]> => {
    const response: ApiResponse<FileSearchStore[]> = await get(
        '/file-search-stores',
        null,
    );

    if (!response.success || !response.data) {
        throw new Error('Failed to fetch file search stores');
    }

    return response.data;
};

/**
 * Get a single file search store by ID
 */
export const getStore = async (
    storeId: string,
): Promise<FileSearchStore> => {
    const response: ApiResponse<FileSearchStore> = await get(
        `/file-search-stores/${storeId}`,
        null,
    );

    if (!response.success || !response.data) {
        throw new Error('Failed to fetch file search store');
    }

    return response.data;
};

/**
 * Create a new file search store
 */
export const createStore = async (
    data: CreateStoreDto,
): Promise<FileSearchStore> => {
    const response: ApiResponse<FileSearchStore> = await post(
        '/file-search-stores',
        data,
    );

    if (!response.success || !response.data) {
        throw new Error('Failed to create file search store');
    }

    return response.data;
};

/**
 * Update a file search store
 */
export const updateStore = async (
    storeId: string,
    data: UpdateStoreDto,
): Promise<FileSearchStore> => {
    const response: ApiResponse<FileSearchStore> = await patch(
        `/file-search-stores/${storeId}`,
        data,
        null,
    );

    if (!response.success || !response.data) {
        throw new Error('Failed to update file search store');
    }

    return response.data;
};

/**
 * Delete a file search store
 */
export const deleteStore = async (storeId: string): Promise<void> => {
    const response: ApiResponse<void> = await remove(
        `/file-search-stores/${storeId}`,
        null,
    );

    if (!response.success) {
        throw new Error('Failed to delete file search store');
    }
};

/**
 * Get documents in a store with pagination
 */
export const getStoreDocuments = async (
    storeId: string,
    pageSize?: number,
    pageToken?: string,
): Promise<Pager<FileSearchDocument>> => {
    const response: ApiResponse<ListDocumentsResponse> = await get(
        `/file-search-stores/${storeId}/documents`,
        {
            pageSize,
            pageToken,
        },
    );

    if (!response.success || !response.data) {
        throw new Error('Failed to fetch documents');
    }

    const { documents, nextPageToken } = response.data;

    const pager: Pager<FileSearchDocument> = {
        page: documents,
        pageInfo: {
            nextPageToken,
        },
        hasNextPage: () => !!nextPageToken,
        nextPage: async () => {
            if (!nextPageToken) {
                return [];
            }
            const nextResponse = await getStoreDocuments(
                storeId,
                pageSize,
                nextPageToken,
            );
            return nextResponse.page;
        },
    };

    return pager;
};

/**
 * Upload a document to a store
 */
export const uploadDocument = async (
    storeId: string,
    file: File,
    onProgress?: (status: string) => void,
): Promise<void> => {
    try {
        onProgress?.('Uploading file...');

        const formData = new FormData();
        formData.append('file', file);

        const response = await instance.post(
            `/file-search-stores/${storeId}/documents/upload`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            },
        );

        const operationName = response.data.data?.operationName;

        if (!operationName) {
            throw new Error('No operation name returned from upload');
        }

        // Poll the operation until it's complete
        onProgress?.('Processing file...');
        let attempts = 0;
        const maxAttempts = 60;
        let done = false;
        let hasError = false;

        while (!done && attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 5000));

            const statusResponse = await instance.get(
                `/file-search-stores/${storeId}/operations/${encodeURIComponent(operationName)}`,
            );
            const status = statusResponse.data.data;
            attempts++;

            done = status.done;
            hasError = !!status.error;

            if (!done && attempts % 6 === 0) {
                onProgress?.(
                    `Still processing... (${Math.floor((attempts * 5) / 60)} min)`,
                );
            }
        }

        if (!done) {
            throw new Error('Upload operation timed out');
        }

        if (hasError) {
            throw new Error('Upload failed');
        }

        onProgress?.('Upload complete!');
    } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
    }
};

/**
 * Delete a document from a store
 */
export const deleteDocument = async (
    storeId: string,
    documentId: string,
): Promise<void> => {
    const response: ApiResponse<void> = await remove(
        `/file-search-stores/${storeId}/documents/${documentId}`,
        null,
    );

    if (!response.success) {
        throw new Error('Failed to delete document');
    }
};
